import { useTranslation } from 'react-i18next';

import type { BatchResult } from '../../api/batch';
import { graphErrorMessage } from '../../utils/graph-error';

function SuccessStatus() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 font-medium text-green-ink">
      <svg className="size-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      {t('batchUpload.status.uploaded')}
    </span>
  );
}

function ErrorStatus({ result }: { result: BatchResult }) {
  const { t } = useTranslation();
  // Une seule résolution i18n par ligne : la valeur servait
  // à la fois de condition et de contenu, donc deux lookups
  // par ligne en erreur.
  const advice = graphErrorMessage(t, result.code);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-medium text-magenta">
        {t('batchUpload.status.error')}
      </span>
      {advice && (
        // Ce que l'opérateur peut faire, dans sa langue.
        // Traduit par `code` et non par `message` : le
        // message de Graph est souvent un ToString() .NET
        // dont le code est la seule partie parlante.
        <span className="text-xs wrap-break-word">{advice}</span>
      )}
      {result.message && (
        // Message technique (souvent l'erreur Graph
        // telle quelle) : ni traduit ni tronqué, c'est
        // ce qu'on colle dans un ticket.
        <span className="mono text-xs wrap-break-word text-muted">
          {result.message}
        </span>
      )}
    </div>
  );
}

function ResultStatus({ result }: { result: BatchResult }) {
  const { t } = useTranslation();
  return (
    <>
      {result.status === 'success' && <SuccessStatus />}
      {result.status === 'pending' && (
        <span
          className="font-medium text-blue"
          title={t('photo.pending.fourEyes')}
        >
          {t('batchUpload.status.pending')}
        </span>
      )}
      {result.status === 'not_found' && (
        <span className="font-medium text-coral">
          {t('batchUpload.status.notFound')}
        </span>
      )}
      {/* Le `message` porte la raison — il était dans un
        `title`, donc invisible au tactile, à la copie et
        au screenshot : un lot entier se lisait « Erreur »
        sans descriptif (#543). Il est affiché. */}
      {result.status === 'skipped' && (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-muted">
            {t('batchUpload.status.skipped')}
          </span>
          {result.message && (
            <span className="text-xs wrap-break-word text-muted-soft">
              {result.message}
            </span>
          )}
        </div>
      )}
      {result.status === 'error' && <ErrorStatus result={result} />}
    </>
  );
}

function ResultsTable({ results }: { results: BatchResult[] }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden border border-rule">
      <div className="max-h-96 overflow-y-auto">
        <table className="epi-table w-full">
          <thead className="sticky top-0 bg-card">
            <tr>
              <th>{t('batchUpload.col.upn')}</th>
              <th>{t('batchUpload.col.user')}</th>
              <th>{t('batchUpload.col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.login}>
                <td className="font-mono text-xs">{r.login}</td>
                <td>
                  {r.displayName ?? (
                    <span className="text-muted-soft italic">—</span>
                  )}
                </td>
                <td>
                  <ResultStatus result={r} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Results of an uploaded batch, with the way back to a new one. */
export function BatchUploadResults({
  results,
  onReset,
}: {
  results: BatchResult[];
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const successCount = results.filter((r) => r.status === 'success').length;
  const pendingCount = results.filter((r) => r.status === 'pending').length;
  const failCount = results.filter(
    (r) => r.status === 'not_found' || r.status === 'error',
  ).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="epi-tag green">
          {t('batchUpload.uploaded', { count: successCount })}
        </span>
        {pendingCount > 0 && (
          <span className="epi-tag blue">
            {t('batchUpload.pendingCount', { count: pendingCount })}
          </span>
        )}
        {failCount > 0 && (
          <span className="epi-tag magenta">
            {t('batchUpload.failed', { count: failCount })}
          </span>
        )}
        <button
          type="button"
          onClick={onReset}
          className="ml-auto text-xs text-muted-soft transition-colors"
        >
          {t('batchUpload.newBatch')}
        </button>
      </div>
      <ResultsTable results={results} />
    </div>
  );
}
